import type React from "react";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type translations from "../../translations/translations.json";
import DataTable from "../common/Table";
import DetailSectionCard from "../common/DetailSectionCard";
import OrderStatusChip from "../common/OrderStatusChip";
import { TestId } from "../../constants/testIds";
import {
  UangJalanReportStatus,
  type UangJalanReport,
  type UangJalanReportSearchFilters,
} from "../../types/uangJalanReport";
import type { OperationsTeam } from "../../types/operationsTeam";
import type { PaginationParam, PaginationResponse } from "../../types/paginationResponse";
import { formatRupiah } from "../../utils/formatRupiah";
import { getOrderStatusAccentColor } from "../../utils/orderStatusAccent";

type TranslationKeys = keyof typeof translations.en;

interface StatusOption {
  value: string;
  label: string;
}

interface UangJalanReportTableProps {
  reports: UangJalanReport[];
  teams: OperationsTeam[];
  filters: UangJalanReportSearchFilters;
  loading?: boolean;
  pagination?: PaginationResponse | null;
  onFiltersChange: (filters: UangJalanReportSearchFilters) => void;
  onSearch: (filters: UangJalanReportSearchFilters, pagination: PaginationParam) => Promise<void>;
  onPaginationChange?: (pagination: PaginationParam) => void;
  canCreateReport?: boolean;
  onCreateReport?: () => void;
}

const REPORT_STATUS_VALUES = Object.values(UangJalanReportStatus);

const filterButtonSx = {
  textTransform: "none" as const,
  fontWeight: 600,
  borderRadius: 1.5,
};

const UangJalanReportTable: React.FC<UangJalanReportTableProps> = ({
  reports,
  teams,
  filters,
  loading,
  pagination,
  onFiltersChange,
  onSearch,
  onPaginationChange,
  canCreateReport = false,
  onCreateReport,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleSearch = async () => {
    await onSearch(filters, { page: 1, pageSize: pagination?.pageSize || 10 });
  };

  const handleClearFilters = async () => {
    const cleared: UangJalanReportSearchFilters = { status: "", teamOperationsId: "" };
    onFiltersChange(cleared);
    await onSearch(cleared, { page: 1, pageSize: pagination?.pageSize || 10 });
  };

  const hasActiveFilters = Boolean(filters.status || filters.teamOperationsId);

  const statusOptions: StatusOption[] = REPORT_STATUS_VALUES.map((status) => ({
    value: status,
    label: t(status as TranslationKeys),
  }));

  const teamOptions = teams.map((team) => ({
    value: team._id,
    label: team.name,
  }));

  const columns = [
    { id: "readableId", label: "ID", sortable: false },
    { id: "status", label: "Status", sortable: false },
    { id: "team", label: "Team", sortable: false },
    { id: "creationCalendarDate", label: "reports.creationCalendarDate", sortable: false },
    { id: "orderCount", label: "reports.orderCount", sortable: false, align: "right" as const },
    { id: "totalPaid", label: "reports.totalPaid", sortable: false, align: "right" as const },
    { id: "difference", label: "reports.difference", sortable: false, align: "right" as const },
  ];

  return (
    <>
      <DetailSectionCard
        title={t("reports.filtersSection" as TranslationKeys)}
        icon={<FilterListOutlinedIcon fontSize="small" />}
        iconColor="#64748B"
        sx={{ mb: 2 }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
            },
            gap: 2,
          }}
        >
          <FormControl fullWidth size="small">
            <Autocomplete
              value={statusOptions.find((option) => option.value === filters.status) || null}
              onChange={(_, newValue) => {
                onFiltersChange({
                  ...filters,
                  status: newValue?.value || "",
                });
              }}
              options={[{ value: "", label: t("All" as TranslationKeys) }, ...statusOptions]}
              getOptionLabel={(option) =>
                option.value === "" ? t("All" as TranslationKeys) : option.label
              }
              renderOption={(props, option) => (
                <li {...props} key={option.value || "all"}>
                  {option.value ? (
                    <OrderStatusChip status={option.value} />
                  ) : (
                    t("All" as TranslationKeys)
                  )}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("Status" as TranslationKeys)}
                  inputProps={{
                    ...params.inputProps,
                    "data-testid": TestId.UJR.STATUS_FILTER,
                  }}
                />
              )}
              size="small"
            />
          </FormControl>
          <FormControl fullWidth size="small">
            <Autocomplete
              value={
                teamOptions.find((option) => option.value === filters.teamOperationsId) || null
              }
              onChange={(_, newValue) => {
                onFiltersChange({
                  ...filters,
                  teamOperationsId: newValue?.value || "",
                });
              }}
              options={[{ value: "", label: "All" }, ...teamOptions]}
              getOptionLabel={(option) =>
                option.value === "" ? t("All" as TranslationKeys) : option.label
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("Team" as TranslationKeys)}
                  inputProps={{
                    ...params.inputProps,
                    "data-testid": TestId.UJR.TEAM_FILTER,
                  }}
                />
              )}
              size="small"
            />
          </FormControl>
        </Box>
        <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="contained" size="small" onClick={handleSearch} sx={filterButtonSx}>
            {t("Search" as TranslationKeys)}
          </Button>
          {hasActiveFilters && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFilters}
              sx={filterButtonSx}
            >
              {t("reports.clearFilters" as TranslationKeys)}
            </Button>
          )}
        </Box>
      </DetailSectionCard>

      <DataTable
        data={reports}
        columns={columns.map((column) => ({
          ...column,
          label: t(column.label as TranslationKeys),
        }))}
        loading={loading}
        showSearch={false}
        pagination={pagination}
        onPaginationChange={onPaginationChange}
        testId={TestId.UJR.TABLE}
        renderRow={(report) => {
          const accent = getOrderStatusAccentColor(report.status);

          return (
            <TableRow
              key={report._id}
              hover
              data-testid={TestId.UJR.ROW}
              onClick={() => navigate(`/uang-jalan-reports/${report.readableId}`)}
              sx={{
                cursor: "pointer",
                borderLeft: 4,
                borderLeftColor: accent,
              }}
            >
              <TableCell
                sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
                data-testid={TestId.UJR.ROW_READABLE_ID(report.readableId)}
              >
                {report.readableId}
              </TableCell>
              <TableCell>
                <OrderStatusChip status={report.status} />
              </TableCell>
              <TableCell>{report.teamOperations?.name || "—"}</TableCell>
              <TableCell>{report.creationCalendarDate}</TableCell>
              <TableCell align="right">
                {report.orderIds.length + (report.standbyOrderIds?.length ?? 0)}
              </TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                {formatRupiah(report.totalPaid)}
              </TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                {formatRupiah(report.difference)}
              </TableCell>
            </TableRow>
          );
        }}
      />

      {reports.length === 0 && !loading && (
        <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: canCreateReport ? 2 : 0 }}>
            {t("reports.noReportsFound" as TranslationKeys)}
          </Typography>
          {canCreateReport && onCreateReport && (
            <Button variant="contained" onClick={onCreateReport} sx={filterButtonSx}>
              {t("reports.emptyAddCta" as TranslationKeys)}
            </Button>
          )}
        </Box>
      )}
    </>
  );
};

export default UangJalanReportTable;
